import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {SliceComponentProps} from '@prismicio/react'
import cn from 'classnames'

export type Explainer3TilesProps = SliceComponentProps<Content.Explainer3TilesSlice>

const paddingTopMap: Record<string, string> = {
  None: '',
  'X-Small (D:16px / M:8px)': 'pt-2 md:pt-4',
  'Small (D:24px / M:16px)': 'pt-4 md:pt-6',
  'Medium (D:48px / M:32px)': 'pt-8 md:pt-12',
  'Large (D:96px / M:48px)': 'pt-12 md:pt-24',
}

const paddingBottomMap: Record<string, string> = {
  None: '',
  'X-Small (D:16px / M:8px)': 'pb-2 md:pb-4',
  'Small (D:24px / M:16px)': 'pb-4 md:pb-6',
  'Medium (D:48px / M:32px)': 'pb-8 md:pb-12',
  'Large (D:96px / M:48px)': 'pb-12 md:pb-24',
}

const Explainer3Tiles = ({slice}: Explainer3TilesProps): JSX.Element => {
  const p = slice.primary
  const isDark = p.background_color === 'Dark green'

  const bgMap: Record<string, string> = {
    'Dark green': 'bg-green-dark',
    'Grey/100': 'bg-moss-grey-100',
    'Moss White': 'bg-moss-white',
  }

  const bgClass = bgMap[p.background_color ?? 'Dark green'] ?? 'bg-green-dark'
  const ptClass = paddingTopMap[p.padding_top ?? 'Large (D:96px / M:48px)'] ?? ''
  const pbClass = paddingBottomMap[p.padding_bottom ?? 'Large (D:96px / M:48px)'] ?? ''

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      data-nav-dark={isDark || undefined}
      className={cn(bgClass, ptClass, pbClass, isDark ? 'text-moss-white' : 'text-moss-black')}
    >
      <div className="mx-auto max-w-[1280px] px-5 md:px-10">
        {p.eyebrow && (
          <p
            className={cn(
              'text-[11px] font-semibold uppercase tracking-widest',
              isDark ? 'text-green-neon' : 'text-green-deep',
            )}
          >
            {p.eyebrow}
          </p>
        )}
        {p.heading && (
          <h2 className={cn('text-h3 md:text-h2 mb-5 mt-4', isDark ? 'text-moss-white' : 'text-moss-black')}>
            {p.heading}
          </h2>
        )}
        {p.subtext && (
          <p
            className={cn(
              'mb-14 max-w-[680px] text-[19px] leading-relaxed',
              isDark ? 'text-moss-white/70' : 'text-black/65',
            )}
          >
            {p.subtext}
          </p>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {p.cards.map((card, i) => (
            <article
              key={i}
              className={cn(
                'flex flex-col rounded-lg border p-7.5',
                isDark ? 'border-moss-white/10 bg-moss-white/[0.06]' : 'border-black/[0.08] bg-black/[0.03]',
              )}
            >
              {card.num && (
                <div className={cn('mb-6 text-[13px] font-semibold', isDark ? 'text-green-neon' : 'text-green-deep')}>
                  {card.num}
                </div>
              )}
              <h3 className={cn('text-h3 mb-4', isDark ? 'text-moss-white' : 'text-moss-black')}>{card.title}</h3>
              {card.description && (
                <p className={cn('text-[16px] leading-[1.55]', isDark ? 'text-moss-white/70' : 'text-black/65')}>
                  {card.description}
                </p>
              )}
              {isFilled.image(card.image) && (
                <div className="mt-auto pt-6">
                  <PrismicNextImage
                    field={card.image}
                    className="aspect-square w-full rounded-lg object-cover"
                    sizes="(max-width: 768px) 100vw, 33vw"
                  />
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

export default Explainer3Tiles
